
export enum Ethnicity {
  Caucasian = 'Caucasian',
  Asian = 'Asian',
  SouthAsian = 'South Asian',
  African = 'African Descent',
  Hispanic = 'Hispanic/Latino',
  PacificIslander = 'Pacific Islander',
  Other = 'Other'
}

export enum Gender {
  Male = 'Male',
  Female = 'Female',
  Other = 'Other'
}

export interface UserProfile {
  name: string;
  email: string;
  age: number;
  gender: Gender;
  ethnicity: Ethnicity;
  location: string;
  heightCm: number;
  weightKg: number;
  conditions: string[]; // e.g. 'Type 2 Diabetes', 'Obesity'
  medications: string[];
  allergies: string[];
  goal: string;
  
  // Gamification
  communityPoints: number;
  communityRank: 'Bronze' | 'Silver' | 'Gold' | 'Platinum';
  currentLevel: number;
  currentXP: number;
  nextLevelXP: number;
  streakDays: number;
  isAdmin?: boolean;
}

export interface HealthMetric {
  date: string;
  weight: number;
  bmi: number;
}

export interface ChatMessage {
  role: 'user' | 'model';
  text: string;
  timestamp: number;
}

export interface Post {
  id: string;
  author: string;
  avatar: string; // Initials or URL
  time: string;
  title: string;
  content: string;
  likes: number;
  comments: number;
  tags: string[];
  isHelpful?: boolean;
}

export interface Facility {
  id: string;
  name: string;
  category: 'gym' | 'restaurant' | 'grocery' | 'medical';
  subcategory: string;
  address: string;
  distance: string; // pre-calculated for MVP
  rating: number;
  reviewCount: number;
  isAffordable: boolean;
  isOpen: boolean;
  tags: string[];
  imageUrl?: string;
  priceRange: '$' | '$$' | '$$$';
}

// Messaging Types
export interface Message {
  id: string;
  senderId: string;
  text: string;
  timestamp: string;
  read: boolean;
  type: 'text' | 'image' | 'system';
}

export interface Conversation {
  id: string;
  name: string;
  avatar: string; // URL or Initials
  role?: string; // e.g. "Dietitian", "Endocrinologist"
  type: 'direct' | 'group' | 'professional';
  lastMessage: string;
  lastMessageTime: string;
  unreadCount: number;
  isOnline?: boolean;
  isVerified?: boolean;
  availabilityStatus?: 'available' | 'away' | 'busy';
}

// Gamification Types
export interface Badge {
  id: string;
  name: string;
  description: string;
  icon: string; 
  category: 'health' | 'community' | 'milestone';
  earnedDate?: string;
  isLocked: boolean;
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
}

export interface Challenge {
  id: string;
  title: string;
  description: string;
  progress: number;
  total: number;
  daysLeft: number;
  rewardPoints: number;
  type: 'daily' | 'weekly';
}

export interface Reward {
  id: string;
  title: string;
  description: string;
  cost: number;
  type: 'discount' | 'content';
}

// Meal Planning Types
export interface Ingredient {
  item: string;
  quantity: string;
  unit: string;
  category: string; // 'Produce', 'Dairy', 'Pantry', 'Protein'
  checked?: boolean;
}

export interface Recipe {
  id: string;
  title: string;
  description: string;
  image?: string; // URL or placeholder emoji
  calories: number;
  macros: { protein: number; carbs: number; fat: number };
  prepTime: number; // minutes
  totalTime: number; // minutes
  tags: string[];
  ingredients: Ingredient[];
  instructions: string[];
  rating: number;
  reviewCount: number;
  priceEstimate: number;
}

export interface MealSlot {
  id: string;
  type: 'breakfast' | 'lunch' | 'dinner' | 'snack';
  recipe: Recipe;
  completed: boolean;
}

export interface DailyPlan {
  date: string; // YYYY-MM-DD
  dayName: string; // 'Mon', 'Tue'
  meals: MealSlot[];
  dailyCalories: number;
}

// Professional Services Types
export interface Professional {
  id: string;
  name: string;
  title: string; // e.g. "Endocrinologist"
  image: string; // URL or initials
  specialties: string[];
  rating: number;
  reviewCount: number;
  pricePerSession: number;
  nextAvailable: string; // "Tomorrow 2PM"
  bio: string;
  verified: boolean;
  languages: string[];
  education: string;
}

export interface Appointment {
  id: string;
  professionalId: string;
  professionalName: string;
  professionalTitle: string;
  professionalImage: string;
  date: string; // YYYY-MM-DD
  time: string;
  duration: number; // minutes
  type: 'video' | 'phone' | 'in-person';
  status: 'upcoming' | 'completed' | 'cancelled';
  notes?: string;
}

// Payment Types
export interface Transaction {
  id: string;
  date: string;
  description: string;
  amount: number;
  status: 'completed' | 'pending' | 'failed';
  method: string; // 'Visa •••• 4242'
  invoiceUrl?: string;
}

export interface PaymentMethod {
  id: string;
  type: 'card' | 'paypal' | 'apple_pay';
  brand?: string;
  last4?: string;
  expiry?: string;
  isDefault: boolean;
}

// Support Types
export interface SupportTicket {
  id: string;
  subject: string;
  category: string;
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  lastUpdated: string;
  priority: 'low' | 'medium' | 'high';
}

export interface Article {
  id: string;
  title: string;
  category: string;
  views: number;
  helpfulCount: number;
}

// Grocery & Restaurant Types
export interface GroceryProduct {
  id: string;
  name: string;
  price: number;
  image: string; // emoji or url
  brand: string;
  size: string;
  healthScore: 'A' | 'B' | 'C' | 'D';
  category: string;
}

export interface Restaurant {
  id: string;
  name: string;
  cuisine: string;
  rating: number;
  deliveryTime: string;
  priceRange: string;
  image: string; // emoji
  isHealthy: boolean;
}

// Analytics Types
export interface MetricData {
  name: string;
  value: number;
  date?: string;
}
